<template>
  <div class="tw-flex tw-flex-col tw-items-center">
    <AppButton
      type="primary"
      :style="{
        backgroundColor: buttonSettings?.bg,
        borderColor: buttonSettings?.bg,
      }"
      :class="[
        'tw-text-black tw-relative landing-btn tw-z-50',
        {
          'landing-btn--animated':
            buttonSettings?.animated &&
            !buttonSettings?.classNames.includes('landing-btn--outlined'),
        },
        ...(buttonSettings?.classNames ?? []),
      ]"
      @click="!previewMode && $emit('create-conversation')"
    >
      <span class="tw-flex tw-items-center tw-justify-center tw-uppercase">
        <cds-icon
          v-if="!buttonSettings?.icon?.isAppended && buttonSettings?.hasIcon"
          :shape="transformedIcon.shape"
          :direction="transformedIcon.direction"
          class="tw-mr-2 landing-btn__icon landing-btn__icon--prepend"
        />
        {{ buttonSettings?.text }}
        <cds-icon
          v-if="buttonSettings?.icon?.isAppended && buttonSettings?.hasIcon"
          :shape="transformedIcon.shape"
          :direction="transformedIcon.direction"
          class="tw-ml-2 landing-btn__icon landing-btn__icon--append"
        />
      </span>
    </AppButton>

    <router-link
      v-if="!previewMode && showOldConversations && route"
      class="tw-mt-2 tw-text-dynamic-primary tw-text-base tw-underline"
      :to="route"
    >
      {{ t('pt.labels.viewOlder') }}
    </router-link>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouteLocationRaw } from 'vue-router'

import { LandingButtonAppSettings } from '@shared/models/interfaces/branding'
import { Nullable } from '@shared/models/types/primary'
import { parseIcon } from '@shared/utils/helpers/icon-parser'

const props = withDefaults(
  defineProps<{
    buttonSettings: Nullable<LandingButtonAppSettings>
    previewMode: boolean
    showOldConversations: boolean
    route?: RouteLocationRaw
  }>(),
  {
    buttonSettings: null,
    previewMode: false,
    showOldConversations: true,
  }
)

defineEmits<{
  (event: 'create-conversation'): void
}>()

const { t } = useI18n()

const transformedIcon = computed(() =>
  parseIcon(props.buttonSettings?.icon.value)
)
</script>

<style lang="scss" scoped>
$btnColor: var(--landing-btn-color);

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 $btnColor;
  }

  70% {
    box-shadow: 0 0 0 10px rgba(0, 0, 0, 0);
  }

  to {
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);
  }
}

.landing-btn {
  min-width: 250px;
  background: $btnColor;

  &--rounded {
    border-radius: 28px;
  }

  &--text {
    border-color: rgba(0, 0, 0, 0.12) !important;
    color: $btnColor !important;
  }

  &--outlined {
    border-color: $btnColor !important;
    color: $btnColor !important;
    background: transparent !important;
  }

  &--tile {
    @apply tw-rounded-none;
  }

  &--animated {
    animation: 1.25s cubic-bezier(0.66, 0, 0, 1) 0s infinite normal none running
      pulse;

    &::before {
      @apply tw-absolute tw-top-0 tw-left-0 tw-right-0 tw-bottom-0;
      background-color: $btnColor;
      content: '';
      z-index: -1;
      border-radius: inherit;
      transition: opacity 0.2s cubic-bezier(0.4, 0, 0.6, 1);
      opacity: 0;
      pointer-events: none;
    }
  }
}
</style>
